import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PlatformDialogSurface } from "./PlatformDialogSurface";
import { PlatformToast, usePlatformToast } from "./PlatformToast";
import { sessionClearedEvent } from "../lib/auth";
import { isNativeApp, nativeErrorEvent } from "../lib/native-app";
import { nativeFileReadyEvent, openNativeFile, shareNativeFile, type NativeFile } from "../lib/native-files";

export function NativeAppHost() {
  const [file, setFile] = useState<NativeFile | null>(null);
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const { toast, showError, showSuccess, dismiss } = usePlatformToast();
  useEffect(() => {
    if (!isNativeApp()) return;
    const ready = (event: Event) => { setFile((event as CustomEvent<NativeFile>).detail); showSuccess("Skedari është gati."); };
    const error = (event: Event) => showError((event as CustomEvent<string>).detail);
    const connection = () => setOffline(!navigator.onLine);
    const clear = () => setFile(null);
    window.addEventListener(sessionClearedEvent, clear);
    window.addEventListener(nativeFileReadyEvent, ready);
    window.addEventListener(nativeErrorEvent, error);
    window.addEventListener("online", connection);
    window.addEventListener("offline", connection);
    return () => {
      window.removeEventListener(sessionClearedEvent, clear);
      window.removeEventListener(nativeFileReadyEvent, ready);
      window.removeEventListener(nativeErrorEvent, error);
      window.removeEventListener("online", connection);
      window.removeEventListener("offline", connection);
    };
  }, [showError, showSuccess]);
  if (!isNativeApp()) return null;
  async function handleFile(action: "open" | "share") {
    if (!file) return;
    setBusy(true);
    try {
      if (action === "open") await openNativeFile(file.uri);
      else await shareNativeFile(file.uri, file.name);
    } catch {
      showError(action === "open" ? "Skedari nuk u hap. Provo ta ruash ose shpërndash." : "Veprimi nuk përfundoi. Mund ta provosh përsëri.");
    } finally { setBusy(false); }
  }
  return <>
    {offline && <div className="native-offline-notice" role="status">Nuk ka lidhje interneti. Rilidhu dhe provo përsëri.</div>}
    {file && createPortal(<PlatformDialogSurface open titleId="native-file-title" busy={busy} onClose={() => setFile(null)} className="native-file-dialog">
      <h2 id="native-file-title">Dokumenti është gati</h2>
      <p className="native-file-name">{file.name}</p>
      <p>Zgjedh “Ruaj ose shpërnda” për të mbajtur një kopje jashtë aplikacionit.</p>
      <div className="native-file-actions">
        <button className="primary-action" disabled={busy} onClick={() => void handleFile("open")}>Hap dokumentin</button>
        <button className="secondary-action" disabled={busy} onClick={() => void handleFile("share")}>Ruaj ose shpërnda</button>
        <button className="secondary-action" disabled={busy} onClick={() => setFile(null)}>Mbyll</button>
      </div>
    </PlatformDialogSurface>, document.body)}
    <PlatformToast toast={toast} onDismiss={dismiss} />
  </>;
}
